import { AppSimpleSelect, AppTextInput } from 'components/FormElements';
import FieldRow from 'components/FormElements/FieldRow';
import { Controller, useFormContext } from 'react-hook-form';
import Spacing from 'components/Spacing';
import { Vehicle } from 'further-types/lp/vehicle';
import { lpCompany as rules } from 'further-ui/validations';
import CurrencySelector from 'components/CurrencySelector';
import CountrySelector from 'components/CountrySelector';

type CoreCompanyDetailsProps = {
  selectedVehicle: Vehicle;
};

function CoreCompanyDetails({ selectedVehicle }: CoreCompanyDetailsProps) {
  const { control, setValue } = useFormContext<rules.CompanySchemaFormValues>();

  return (
    <Spacing>
      <FieldRow title="Vehicle" centerTitle>
        <Controller
          name="vehicleId"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <AppSimpleSelect
              fullWidth
              disabled
              options={[
                {
                  value: selectedVehicle._id,
                  label: selectedVehicle.vehicleName,
                },
              ]}
              {...rest}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Trading name*">
        <Controller
          name="tradingName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              fullWidth
              placeholder="Enter trading name, e.g. Acme"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Full legal name*">
        <Controller
          name="fullLegalName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              placeholder="Enter legal name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Base currency*">
        <Controller
          name="baseCurrency"
          defaultValue={selectedVehicle.baseCurrency}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <CurrencySelector
              {...field}
              placeholder="Select base currency"
              error={error ? { message: error.message } : undefined}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registered number">
        <Controller
          name="registeredNumber"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              placeholder="Registered number"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registration country">
        <Controller
          name="registrationCountry"
          control={control}
          defaultValue=""
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <CountrySelector
              {...rest}
              ref={ref}
              value={value ?? ''}
              disableAutoFill
              name="registrationCountry"
              placeholder="Select registration country"
              error={error}
              onChange={(_, option) => {
                setValue('registrationCountry', option?.code || '');
              }}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registered address line 1" centerTitle>
        <Controller
          name="registeredAddressLine1"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Enter registered address 1"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registered address line 2" centerTitle>
        <Controller
          name="registeredAddressLine2"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Enter registered address 2"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registered address town/city" centerTitle>
        <Controller
          name="registeredAddressTownCity"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Enter registered address town/city"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registered address postal code" centerTitle>
        <Controller
          name="registeredAddressPostalCode"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Enter registered address postal code"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Registered address country" centerTitle>
        <Controller
          name="registeredAddressCountry"
          control={control}
          defaultValue=""
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <CountrySelector
              {...rest}
              ref={ref}
              value={value || ''}
              name="registeredAddressCountry"
              placeholder="Select registered address country"
              disableAutoFill
              error={error}
              onChange={(_, option) => {
                setValue('registeredAddressCountry', option?.code || '');
              }}
            />
          )}
        />
      </FieldRow>
    </Spacing>
  );
}

export default CoreCompanyDetails;
